import request from '../utils/request';
import { ApiResponse } from '../types';

/**
 * 登录请求参数（密码登录：account 为用户名；短信登录：account 为手机号）
 */
export interface LoginParams {
  account: string; // 密码登录时为用户名，短信登录时为手机号
  password: string; // 密码（仅密码登录时使用）
}

/**
 * 登录响应数据
 */
export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    nickname: string;
    avatar?: string;
    email?: string;
    phone?: string;
    registerTime: string;
    remainingChats: number; // 剩余对话次数
    role?: UserRole;
  };
}

/**
 * 用户角色
 */
export type UserRole = 'user' | 'admin';

/**
 * 用户信息
 */
export interface UserInfo {
  id: string;
  username: string;
  nickname: string;
  avatar?: string;
  email?: string;
  phone?: string;
  registerTime: string;
  remainingChats: number;
  role?: UserRole;
}

/**
 * 修改用户信息参数
 */
export interface UpdateUserParams {
  nickname?: string;
  avatar?: string;
}

/**
 * 修改密码参数
 */
export interface ChangePasswordParams {
  oldPassword: string;
  newPassword: string;
}

/**
 * 发送验证码参数
 */
export interface SendCodeParams {
  account: string; // 手机号（或邮箱，仅 register 时）
  type: 'login' | 'register' | 'reset' | 'bind'; // 验证码类型
}

/**
 * 注册请求参数（用户名 + 密码）
 */
export interface RegisterParams {
  username: string;
  password: string;
}

/**
 * 注册响应数据
 */
export interface RegisterResponse {
  token: string;
  user: {
    id: string;
    username: string;
    nickname: string;
    avatar?: string;
    email?: string;
    phone?: string;
    registerTime: string;
    remainingChats: number; // 剩余对话次数
    role?: UserRole;
  };
}

/**
 * 登录接口
 */
export const login = async (params: LoginParams): Promise<ApiResponse<LoginResponse>> => {
  return request.post('/users/login', params);
};

/**
 * 注册接口
 */
export const register = async (params: RegisterParams): Promise<ApiResponse<RegisterResponse>> => {
  return request.post('/users/register', params);
};

/**
 * 获取用户信息
 */
export const getUserInfo = async (): Promise<ApiResponse<UserInfo>> => {
  return request.get('/users/profile');
};

/**
 * 更新用户信息
 */
export const updateUserInfo = async (params: UpdateUserParams): Promise<ApiResponse<UserInfo>> => {
  return request.put('/users/profile', params);
};

/**
 * 修改密码（旧密码方式）
 */
export const changePassword = async (params: ChangePasswordParams): Promise<ApiResponse<void>> => {
  return request.put('/users/password', params);
};

/**
 * 发送修改密码验证码（仅发往当前账号已绑定的手机号）
 */
export const sendChangePasswordCode = async (phone: string): Promise<ApiResponse<{ mockCode?: string } | null>> => {
  return request.post('/users/password/send-change-code', { phone });
};

/**
 * 通过手机验证码修改密码
 */
export interface ChangePasswordByCodeParams {
  phone: string;
  code: string;
  newPassword: string;
}
export const changePasswordByCode = async (params: ChangePasswordByCodeParams): Promise<ApiResponse<void>> => {
  return request.put('/users/password/change-by-code', params);
};

/**
 * 发送验证码（仅支持手机号）。Mock 未真实发送时 data 含 mockCode 供前端弹窗显示
 */
export const sendCode = async (params: SendCodeParams): Promise<ApiResponse<{ mockCode?: string } | null>> => {
  return request.post('/auth/send-code', params);
};

/**
 * 验证码登录参数
 */
export interface LoginByCodeParams {
  account: string; // 手机号
  code: string; // 6位验证码
}

/**
 * 验证码登录
 */
export const loginByCode = async (params: LoginByCodeParams): Promise<ApiResponse<LoginResponse>> => {
  return request.post('/users/login-by-code', params);
};

/**
 * 短信找回密码参数
 */
export interface ResetPasswordParams {
  account: string; // 手机号
  code: string;
  newPassword: string;
}

/**
 * 短信找回密码
 */
export const resetPassword = async (params: ResetPasswordParams): Promise<ApiResponse<void>> => {
  return request.post('/users/password/reset', params);
};

/**
 * 检查手机号是否已绑定用户（短信登录前调用）
 */
export const checkPhoneBound = async (phone: string): Promise<ApiResponse<{ bound: boolean }>> => {
  return request.get('/users/check-phone', { params: { phone } });
};

/**
 * 发送绑定手机号验证码（发短信前会校验：已绑定其他账号或本账号则不发）
 * Mock 时 data 含 mockCode 供前端弹窗显示
 */
export const sendBindPhoneCode = async (phone: string): Promise<ApiResponse<{ mockCode?: string }>> => {
  return request.post('/users/bind-phone/send-code', { phone });
};

/**
 * 已登录用户绑定手机号
 */
export interface BindPhoneParams {
  phone: string;
  code: string;
}
export const bindPhone = async (params: BindPhoneParams): Promise<ApiResponse<UserInfo>> => {
  return request.put('/users/bind-phone', params);
};

/**
 * 退出登录（前端本地清除token即可，无需调用后端）
 */
export const logout = async (): Promise<ApiResponse<void>> => {
  // 退出登录主要是前端清除token，不需要调用后端API
  // 如果需要服务端清除session等，可以在这里添加API调用
  return Promise.resolve({ code: 200, message: 'success', data: undefined });
};
